/**
 * 지정한 시간(ms) 동안만 값을 보관하는 메모리 캐시
 *
 * - 메모리에만 보관하므로 새로고침·새 탭에서는 항상 비어 있습니다.
 * - 같은 시점에 여러 곳에서 요청해도 진행 중인 요청 하나를 함께 기다립니다.
 * - 실패한 결과는 보관하지 않습니다.
 */
export class TimedCache<T> {
  private value: T | null = null;
  private expiresAt = 0;
  private pending: Promise<T> | null = null;
  private generation = 0;

  constructor(
    private readonly ttlMs: number,
    private readonly now: () => number = () => Date.now()
  ) {}

  /**
   * 유효한 캐시가 있으면 반환하고, 없으면 loader 로 받아와 성공 시 보관합니다.
   * @param loader 값을 받아오는 함수
   * @param shouldCache 받아온 값을 보관할지 여부 (실패 결과 제외용)
   */
  async getOrLoad(loader: () => Promise<T>, shouldCache: (value: T) => boolean): Promise<T> {
    if (this.value !== null && this.now() < this.expiresAt) {
      return this.value;
    }

    if (this.pending) {
      return this.pending;
    }

    const generation = this.generation;
    this.pending = (async () => {
      try {
        const loaded = await loader();
        // 요청 도중 invalidate 되었으면 오래된 값을 보관하지 않음
        if (generation === this.generation && shouldCache(loaded)) {
          this.value = loaded;
          this.expiresAt = this.now() + this.ttlMs;
        }
        return loaded;
      } finally {
        if (generation === this.generation) {
          this.pending = null;
        }
      }
    })();

    return this.pending;
  }

  /**
   * 보관 중인 값을 비웁니다 (데이터 변경 후 호출).
   */
  invalidate(): void {
    this.value = null;
    this.expiresAt = 0;
    this.pending = null;
    this.generation += 1;
  }
}
